/**
 * BirthdaysPanel
 * People in the company with a birthday from today up to the same day next month.
 * GET /api/birthdays/ (via birthdaysApi — shared with the "Birthdays Today" KPI card)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Cake, RefreshCw, Loader2, PartyPopper } from 'lucide-react';
import { fetchBirthdays, formatBirthdayDate, whenLabel } from './birthdaysApi';

const initials = (name) =>
  (name || '').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const userColor = (name) =>
  COLORS[(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

export const BirthdayAvatar = ({ person, size = 28 }) => {
  const name = person.full_name || '';
  if (person.profile_picture) {
    return (
      <img src={person.profile_picture} alt={name}
        className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 text-white"
      style={{ width: size, height: size, backgroundColor: userColor(name), fontSize: size * 0.36, fontWeight: 700 }}>
      {initials(name)}
    </div>
  );
};

const BirthdaysPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback((force = false) => {
    setLoading(true); setError(null);
    fetchBirthdays({ force })
      .then(setItems)
      .catch(() => setError('Could not load birthdays.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const todayCount = items.filter((p) => p.is_today).length;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden" data-birthdays-panel>

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Cake size={13} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Upcoming Birthdays</span>
          {!loading && !error && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-steel-100 text-slate-500">
              next month · {items.length}
            </span>
          )}
          {!loading && todayCount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-600">
              {todayCount} today
            </span>
          )}
        </div>
        <button onClick={() => load(true)} title="Refresh"
          className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <Cake size={28} />
          <span className="text-xs">No birthdays in the next month</span>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          {items.map((p) => (
            <div key={p.id} data-birthday-row data-today={p.is_today ? 'true' : undefined}
              className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 transition-colors ${
                p.is_today ? 'bg-pink-50/60 hover:bg-pink-50' : 'hover:bg-steel-50'
              }`}>
              <BirthdayAvatar person={p} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-700 truncate leading-tight">
                  {p.full_name}
                  {p.is_me && <span className="ml-1 font-normal text-slate-400">(you)</span>}
                </p>
                <p className="text-[11px] text-slate-400 leading-tight">{formatBirthdayDate(p.date)}</p>
              </div>
              {p.is_today ? (
                <span className="flex items-center gap-1 text-[11px] font-semibold text-pink-600 flex-shrink-0">
                  <PartyPopper size={12} /> Today
                </span>
              ) : (
                <span className="text-[11px] text-slate-500 flex-shrink-0">{whenLabel(p.days_until)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BirthdaysPanel;
